// Small presentational badges shared across the Service Desk (portal + agent).
import { useTranslation } from "react-i18next";
import {
  SLA_META,
  PRIORITY_META,
  AI_AGENT_META,
  type SlaState,
  type TicketPriority,
  type AiAgentState,
} from "../services/serviceDeskService";
import { REWORK_META } from "../services/ticketClosure";

export function SlaBadge({ state }: { state?: SlaState }) {
  const meta = SLA_META[state || "unknown"];
  const isBreached = state === "breached";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isBreached ? "ring-1 ring-red-300" : ""}`}
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      {isBreached ? <span aria-hidden="true">⚠</span> : <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />}
      SLA: {meta.label}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: `${meta.color}1a` }}
    >
      {meta.label}
    </span>
  );
}

/**
 * Estado del agente IA sobre el ticket. `idle` no se pinta: un ticket sin
 * trabajar es el caso normal y llenar el tablero de chips grises no informa.
 */
export function AiAgentBadge({ state }: { state?: AiAgentState }) {
  if (!state || state === "idle") return null;
  return <AiAgentChip state={state} />;
}

function AiAgentChip({ state }: { state: AiAgentState }) {
  const { t } = useTranslation("serviceDesk");
  const meta = AI_AGENT_META[state];
  if (!meta) return null;
  const label = t(`ticketDetail.aiAgent.states.${state}`, meta.label);
  const title = t("ticketDetail.aiAgent.title", "Agente IA");
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg }}
      title={`${title}: ${label}`}
    >
      <span aria-hidden>&#9881;</span>
      {t("ticketDetail.aiAgent.badgePrefix", "IA")}: {label}
    </span>
  );
}

/**
 * «Rehecho ×N» (#729): cuántas veces el ticket volvió de «Resuelto» porque no
 * estaba resuelto. En 0 no se pinta — es el caso normal, igual que `idle` en
 * el agente IA.
 */
export function ReworkChip({ count }: { count?: number | null }) {
  const { t } = useTranslation("serviceDesk");
  if (!count || count < 1) return null;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ color: REWORK_META.color, backgroundColor: `${REWORK_META.color}1a` }}
      title={t("closure.reworkChipTitle", "Veces que se regresó a «Rehacer» porque no quedó resuelto")}
    >
      {/* `n` y no `count`: i18next trata `count` como plural y buscaría _one/_other. */}
      {t("closure.reworkChip", "Rehecho ×{{n}}", { n: count })}
    </span>
  );
}

export function StatusBadge({ label, color }: { label: string; color?: string }) {
  const c = color || "#6b7280";
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ color: c, backgroundColor: `${c}1a` }}
    >
      {label}
    </span>
  );
}

export function LabelChip({ text, color }: { text: string; color?: string }) {
  const c = color || "#64748b";
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium"
      style={{ color: c, backgroundColor: `${c}1a` }}
    >
      {text}
    </span>
  );
}
